<script>
import DateTime from '../../../functions/lib/dateTime.js';
import { createDirectionControl, loadMaps } from '../../js/googleMaps.js';
import { commands } from '../../js/lib/functions.js';
import { listenToClubCalendarView, listenToClubView, listenToCurrentUserView } from '../../js/readModel.js';
import { showInput } from '../../js/utils/input.js';
import { showToast } from '../../js/utils/toast.js';


export const pageName = 'clubs-view';
export const pageRoute = '/clubs/{id}';
export const title = 'View Clubs'
export const requireMembership = true;

export default {
	data() {
		return { 
			currentUser: null,
			club: null,
			calendar: null,
			notes: null,
			events: [],
			showingAllEvents: false,
			year: DateTime.today.year,
			ready: false
		}
	},

	methods: {
		async boot(opts) {
			await listenToCurrentUserView(async user => {
				this.currentUser = user;
			});

			let mapsP = loadMaps();
			
			await listenToClubView(opts.id, club => {
				this.club = club;
				this.notes = (this.club.notes || '').trim().split('\n').filter(l => l.length).map(l => `<p>${l}</p>`).join('');

				if(this.marker)
					this.marker.position = club.coords;

				if(this.map)
				{
					this.map.setZoom(16);
					this.map.setCenter(club.coords);
				}
			});
			let calendarId = this.club.calendars[DateTime.today.year];
			if(calendarId)
			{
				await listenToClubCalendarView(this.club.id, calendarId, cal => {
					this.calendar = cal;
					this.events = this.calendar.futureEvents
				});
			}
			await mapsP;
			this.ready = true;
		},
		async show()
		{
			if(!this.map)
			{			
				const { Map } = await google.maps.importLibrary("maps");
				this.map = new Map(this.$refs.map, {
					center: this.club.coords,
					zoom: 16,
					disableDoubleClickZoom: true,
					options: {
						gestureHandling: 'greedy'
					},
					mapId: this.club.acronym + '_View',
  					mapTypeId: 'hybrid',
					zoomControl: false,
					cameraControl: false,
					mapTypeControl: true,
					scaleControl: false,
					streetViewControl: true,
					rotateControl: false,
					fullscreenControl: true
				});

				this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(createDirectionControl(this.map, this.club.coords));

				const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
				this.marker = new AdvancedMarkerElement({
					map: this.map,
					position: this.club.coords,
					title: this.club.name,
				});
			}
			this.showingAllEvents = false;
		},
		showPastEvents() {
			this.events = this.calendar.allEvents;
			this.showingAllEvents = true;
		},
		setEventStatus(club, event)
		{
			commands.warmUp.clubSetCalendarEventStatus();

			showInput({
				title: 'Set event status',
				message: `Set whether this event is going ahead or not.<br>All subscribers will be emailed and messages will be posted to socials.`,
				input: {
					label: 'A public-facing message to flyers',
					type: 'text',
					required: true,
					placeholder: 'e.g. We are go for launch!'
				},
				buttons: [
					{
						text: 'Close',
						style: 'white'
					},
					{
						text: 'Cancel event',
						style: 'outline-danger',
						action: async notes => {
							let result = await commands.clubSetCalendarEventStatus({
								clubId: club.id,
								calendarId: this.calendar.id,
								eventId: event.id,
								status: 'cancelled',
								notes
							})
							if(!result.success)
								showToast({ message: 'Error setting event status', style: 'bg-danger'})
							else
								showToast({ message: 'Event status set', style: 'bg-success'})
						}
					},
					{
						text: 'Confirm event',
						style: 'success',
						action: async notes => {
							let result = await commands.clubSetCalendarEventStatus({
								clubId: club.id,
								calendarId: this.calendar.id,
								eventId: event.id,
								status: 'confirmed',
								notes
							})
							if(!result.success)
								showToast({ message: 'Error setting event status', style: 'bg-danger'})
							else
								showToast({ message: 'Event status set', style: 'bg-success'})
						}
					}
				]
			})
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 my-5">
				<h1 class="display-4 text-center mb-3">{{ club.name }}</h1>
				<p class="text-muted text-center mb-5">View details about this club.</p>
				<a v-if="currentUser.isAdminOfClub(club.id)" :href="`/clubs/${club.id}/edit`" class="btn btn-outline-primary btn-block mb-4">Edit club <span class="fe fe-edit-2"></span></a>
				<div class="text-center mb-4">
					<img :src="club.logoUrl" :alt="club.name" />
				</div>
				<div v-html="notes"></div>
				<h2>Launch site</h2>
				<div class="map mb-4" ref="map" style="height: 400px"></div>
				<hr class="my-5">
				<h2>{{ year }} event calendar</h2>
				
				<div class="row">
					<div class="col">
						<a v-if="currentUser.isAdminOfClub(club.id)" :href="`/clubs/${club.id}/${calendar.id}/events/add-regular`" class="btn btn-outline-primary btn-block mb-4">Add regular event <span class="fe fe-plus-circle"></span></a>
					</div>
					<div class="col">
						<a v-if="currentUser.isAdminOfClub(club.id)" :href="`/clubs/${club.id}/${calendar.id}/events/add-special`" class="btn btn-outline-primary btn-block mb-4">Add special event <span class="fe fe-plus-circle"></span></a>
					</div>
				</div>
				<button v-if="!showingAllEvents" class="btn btn-outline-secondary btn-block mb-4" @click="showPastEvents">Show past events</button>

				<div v-for="event in events" class="card">
					<div class="card-header">
						<h4 class="card-header-title">{{ event.name }}</h4>
						<div>
							<a v-if="currentUser.isAdminOfClub(club.id)" :href="`/clubs/${club.id}/events/${event.id}/edit`" class="btn btn-sm btn-outline-primary mr-2">Edit <span class="fe fe-edit-2"></span></a>
							<button v-if="currentUser.isAdminOfClub(club.id)" @click.prevent="setEventStatus(club, event)" :class="`btn btn-sm ${event.status == 'planned' ? 'btn-primary' : 'btn-outline-secondary'}`">Set status <span class="fe fe-help-circle"></span></button>
						</div>
					</div>
					<div class="card-body">
						<h4 v-if="event.status == 'planned'" class="alert alert-light"><span class="fe fe-help-circle text-primary"></span> Planned: Check back or <a href="/account/preferences"><u>subscribe to notifications</u></a></h4>
						<h4 v-if="event.status == 'confirmed'" class="alert alert-success"><span class="fe fe-check-circle "></span> Confirmed<span v-if="event.statusNotes">: {{ event.statusNotes }}</span></h4>
						<h4 v-if="event.status == 'cancelled'" class="alert alert-danger"><span class="fe fe-x-circle"></span> Cancelled<span v-if="event.statusNotes">: {{ event.statusNotes }}</span></h4>
						
						<p>{{ event.date }}</p>
						
						<p v-if="event.type == 'regular'">Regular flying day</p>
						<p v-if="event.type == 'special'">Special event</p>
						<p v-if="event.notes">{{ event.notes }}</p>
					</div>
				</div>

			</div>
		</div>
    </div>
</template>
